import {
    Button,
    Caption,
    Flex,
    Grid,
    Label,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import { CloseLine, HeartFill, HeartLine } from '@archipro-design/icons';
import { useNavigate } from 'react-router';
import type {
    ContactLoaderData,
    LocationDataType,
} from '@modules/professional/type/contact-page-types';
import {
    buildLocationRevealExtraData,
    getFullAddress,
    getLocationProfessional,
} from '@modules/professional/util/contact-page-helper';
import { FollowSelector } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import * as S from './LocationMapPopup.style';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import ClickToView from '~/modules/root/component/click-to-view/ClickToView';
import { parsePhone } from '~/modules/root/util/phone';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { isUserLoggedIn, useUser } from '@modules/user';
import { useGuestSave } from '~/modules/moodboards/hooks/use-guest-save';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { SpecifierLoginPrompt } from '~/modules/enquiry/component/specifier-login-prompt/SpecifierLoginPrompt';

interface LocationMapPopupProps {
    clickedLocation: LocationDataType;
    onMapPopupClose?: () => void;
    linkedProfiles: ContactLoaderData['linkedProfiles'];
}

export const LocationMapPopup = ({
    clickedLocation: location,
    onMapPopupClose,
    linkedProfiles,
}: LocationMapPopupProps) => {
    const { css } = useStyles({ hasOnlineOrder: location.AllowsPickup });
    const navigate = useNavigate();
    const initialData = useProfessionalInitialRouteData();
    const { showEnquiryCTA, showSpecifierLoginPrompt } =
        useEnquiryVisibility(initialData);

    const onViewProfile = (
        e: React.SyntheticEvent<HTMLElement, Event>,
        routePath: string,
    ) => {
        e.preventDefault();
        navigate(routePath);
    };

    const locationProfessional = getLocationProfessional(
        location,
        initialData,
        linkedProfiles,
    );

    const phoneParsed = location.Phone
        ? parsePhone(location.Phone, location.Country)
        : null;

    const { isInDesignBoard } = useHandleFollow();
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { guestSave } = useGuestSave();

    const { openEnquiryModal } = useEnquiryModal({
        professional: initialData,
        itemType: 'professional',
        siteTreeID: initialData.ID,
        source: 'ProfessionalLocationMap',
        includeBranch: true,
    });
    const enquiryLabel = 'Enquire';

    return (
        <Grid columns={1} className={css(S.MapPopupWrapper)}>
            <Flex space="between" hAlign="end" className={css(S.LocationType)}>
                <div>{location.Type}</div>

                <Button
                    onClick={onMapPopupClose}
                    transparent={true}
                    icon={<CloseLine size="2x" />}
                    size={30}
                    variables={{
                        buttonPadding: 4,
                    }}
                />
            </Flex>

            <div className={css(S.LocationTitle)}>
                <Caption variant="03">{location.Title}</Caption>
            </div>

            <Flex column className={css(S.LocationAddress)}>
                <ClickToView
                    clickLabel="Address"
                    fulltext={getFullAddress(location)}
                    coordinates={location.MapCoordinates}
                    type={'Location'}
                    label={initialData.Title}
                    buttonTrackingID={
                        locationProfessional.PageType.toLowerCase() +
                        '-contact-accordion-address-reveal'
                    }
                    revealKey={`professional-${locationProfessional.ID}`}
                    revealSubKey={location.ID.toString()}
                    extraData={buildLocationRevealExtraData(location)}
                />
            </Flex>
            <Flex column className={css(S.LocationPhone)}>
                <ClickToView
                    clickLabel="Phone"
                    fulltext={location.Phone || ''}
                    telHref={phoneParsed?.telUri}
                    type={'Phone'}
                    label={initialData.Title}
                    buttonTrackingID={
                        locationProfessional.PageType.toLowerCase() +
                        '-library-location-phone-reveal'
                    }
                    revealKey={`professional-${locationProfessional.ID}`}
                    // The page's professional, not the location's — a branch
                    // location can belong to a linked profile, and the ad that
                    // brought the visitor here was for the page.
                    professionalId={initialData.ID}
                />
            </Flex>

            {location.AllowsPickup && (
                <Flex column className={css(S.ClickAndCollectAvailable)}>
                    <Label variant="02">Click & collect available</Label>
                </Flex>
            )}

            <Grid
                columns={`1fr ${pxToRem(48)} 1fr`}
                className={css(S.EnquiryAndSaveWrapper)}
            >
                <div>
                    {showEnquiryCTA && (
                        <Button
                            color="dark"
                            size={24}
                            className={css(S.EnquireButton)}
                            variables={{
                                darkButtonBackgroundColorNormal: '#222222',
                            }}
                            onClick={() =>
                                openEnquiryModal({
                                    title: enquiryLabel,
                                    ctaLabel: enquiryLabel,
                                })
                            }
                        >
                            {enquiryLabel}
                        </Button>
                    )}
                </div>
                {(() => {
                    const isSaved = isInDesignBoard(
                        locationProfessional.ID,
                        'ProfessionalPin',
                    );
                    const heartButton = (
                        <Button
                            transparent
                            size={16}
                            className={css(S.SaveToDesignBoardButton)}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (!isLoggedIn) {
                                    guestSave({
                                        pinId: locationProfessional.ID,
                                        pinType: 'ProfessionalPin',
                                    });
                                }
                            }}
                            icon={
                                isSaved ? (
                                    <HeartFill size="1x" />
                                ) : (
                                    <HeartLine size="1x" />
                                )
                            }
                        />
                    );
                    return isLoggedIn ? (
                        <FollowSelector
                            trigger={heartButton}
                            pinId={locationProfessional.ID}
                            pinType="ProfessionalPin"
                        />
                    ) : (
                        heartButton
                    );
                })()}

                <Flex hAlign="center">
                    <Button
                        as={'a'}
                        href={`/professional/${locationProfessional.URLSegment}`}
                        color="dark"
                        transparent={true}
                        size={16}
                        variables={{
                            buttonPadding: 0,
                        }}
                        className={css(S.ViewProfileButton)}
                        onClick={(e) =>
                            onViewProfile(
                                e,
                                `/professional/${locationProfessional.URLSegment}`,
                            )
                        }
                    >
                        View Profile
                    </Button>
                </Flex>
            </Grid>
            {showSpecifierLoginPrompt && (
                <SpecifierLoginPrompt
                    professionalName={initialData.Title}
                    itemDescription="this professional"
                    showBottomBorder={false}
                    className="mt-4 py-0"
                />
            )}
        </Grid>
    );
};
